import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';

import { SECTION_LABEL_CLASS } from '../ui/ui.classes';
import { ChipComponent } from './chip.component';
import { DECISION_LABEL_KEY, DECISION_TONE } from './guarantee-vocabulary';
import { RecordedNote } from './recorded-notes';

/**
 * One recorded note: what it explains, then what it says.
 *
 * Which line it belongs to is stated only where notes from several are read together — beside a
 * line's own facts it is already known, and repeating it there would be the loudest thing in the
 * block. The ruling rides along for the same reason a note has a label at all: a refusal and a
 * grant can carry the same words, and only the chip beside them says which was given.
 */
@Component({
  selector: 'guarantees-recorded-note',
  standalone: true,
  imports: [TranslatePipe, ChipComponent],
  host: { class: 'block' },
  template: `
    <div class="flex flex-col gap-0.5 rounded-lg bg-neutral-50 px-2 py-1.5">
      @if (prescriptionName()) {
        <span class="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span class="min-w-0 flex-1 truncate text-sm font-semibold text-neutral-700">{{ prescriptionName() }}</span>
          @if (decisionLabelKey(); as decisionLabelKey) {
            <guarantees-chip class="shrink-0" variant="outline" [tone]="decisionTone()">{{ decisionLabelKey | translate }}</guarantees-chip>
          }
        </span>
      }
      <span [class]="labelClass">{{ note().labelKey | translate }}</span>
      <span class="text-sm text-neutral-600">{{ note().text }}</span>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecordedNoteComponent {
  note = input.required<RecordedNote>();
  /** The line the note was recorded against. Left unset where that is already known. */
  prescriptionName = input<string>('');

  protected readonly labelClass = SECTION_LABEL_CLASS;

  protected readonly decisionLabelKey = computed(() => {
    const decision = this.note().decision;

    return decision ? DECISION_LABEL_KEY[decision] : '';
  });

  protected readonly decisionTone = computed(() => {
    const decision = this.note().decision;

    return decision ? DECISION_TONE[decision] : 'neutral';
  });
}
